import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AddPortChargeModalComponent } from '../add-port-charge-modal/add-port-charge-modal';
import { Carrier } from '../../../../../models/forwarding/carrier.model';
import { CarrierService } from '../../../../../services/forwarding/carrier.service';

interface PortCharge {
  id: number;
  agent: string;
  port: string;
  portCode: string;
  chargeType: string;
  validFrom: Date;
  validTo?: Date;
  currency: string;
  status: 'Active' | 'Inactive' | 'Expired';
  hasCarrierCharges: boolean;
}

@Component({
  selector: 'app-port-charges-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, AddPortChargeModalComponent],
  templateUrl: './port-charges-tab.html',
  styleUrl: './port-charges-tab.css'
})
export class PortChargesTabComponent implements OnInit {
  // Filter values
  searchTerm = '';
  selectedAgent = 'All Agents';
  selectedPort = 'All Ports';
  selectedType = 'All Types';
  showAdvancedFilters = false;

  // Sort
  sortBy = 'Valid From';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Carrier-Specific Charges Modal
  showCarrierChargesModal = false;
  selectedPortCharge: PortCharge | null = null;
  carriers: Carrier[] = [];
  selectedCarriersForCharges: Carrier[] = [];
  availableCarriersForSelection: Carrier[] = [];
  selectedCarrierToAdd: number | string = '';

  // Sample data
  portCharges: PortCharge[] = [
    {
      id: 1,
      agent: 'Global Freight Ltd.',
      port: 'Shanghai Port',
      portCode: 'CNSHA',
      chargeType: 'Origin',
      validFrom: new Date('2025-08-06'),
      validTo: undefined,
      currency: 'USD',
      status: 'Active',
      hasCarrierCharges: true
    }
  ];

  // Dropdown options
  agents = ['All Agents', 'Global Freight Ltd.', 'Maritime Solutions', 'Ocean Express'];
  ports = ['All Ports', 'Shanghai Port (CNSHA)', 'Manila (PHMNL)', 'Batangas (PHBTG)'];
  types = ['All Types', 'Origin', 'Destination', 'Transit'];

  // Modal states
  showAddPortChargeModal = false;

  constructor(private carrierService: CarrierService) {}

  ngOnInit(): void {
    this.loadCarriers();
    console.log('[PortChargesTab] Component initialized');
  }

  get filteredPortCharges(): PortCharge[] {
    return this.portCharges.filter(charge => {
      const matchesSearch = !this.searchTerm ||
        charge.agent.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        charge.port.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        charge.portCode.toLowerCase().includes(this.searchTerm.toLowerCase());

      const matchesAgent = this.selectedAgent === 'All Agents' || charge.agent === this.selectedAgent;
      const matchesPort = this.selectedPort === 'All Ports' || `${charge.port} (${charge.portCode})` === this.selectedPort;
      const matchesType = this.selectedType === 'All Types' || charge.chargeType === this.selectedType;

      return matchesSearch && matchesAgent && matchesPort && matchesType;
    });
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  openAddPortChargeModal(): void {
    this.showAddPortChargeModal = true;
  }

  closeAddPortChargeModal(): void {
    this.showAddPortChargeModal = false;
  }

  savePortCharge(data: any): void {
    console.log('[PortChargesTab] Saving new port charge:', data);
    // Here you would normally call the API to save the port charge
    // For now, just close the modal
    this.showAddPortChargeModal = false;
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatValidity(validFrom: Date, validTo?: Date): string {
    const from = this.formatDate(validFrom);
    const to = validTo ? this.formatDate(validTo) : '∞';
    return `${from} — ${to}`;
  }

  viewCharge(charge: PortCharge): void {
    console.log('[PortChargesTab] View charge:', charge);
  }

  editCharge(charge: PortCharge): void {
    console.log('[PortChargesTab] Edit charge:', charge);
  }

  deleteCharge(charge: PortCharge): void {
    if (confirm(`Delete port charge for ${charge.agent} at ${charge.port}?`)) {
      this.portCharges = this.portCharges.filter(c => c.id !== charge.id);
    }
  }

  manageCarrierCharges(charge: PortCharge): void {
    console.log('[PortChargesTab] Manage carrier-specific charges:', charge);
    this.selectedPortCharge = charge;
    this.selectedCarriersForCharges = [];
    this.selectedCarrierToAdd = '';

    // Get all active carriers
    this.availableCarriersForSelection = this.carriers.filter(c => c.isActive);

    this.showCarrierChargesModal = true;
    console.log('[PortChargesTab] Carrier-Specific Charges modal opened for:', charge.agent, charge.port);
  }

  closeCarrierChargesModal(): void {
    this.showCarrierChargesModal = false;
    this.selectedPortCharge = null;
    this.selectedCarriersForCharges = [];
    this.availableCarriersForSelection = [];
    this.selectedCarrierToAdd = '';
  }

  addCarrierToSelection(): void {
    if (!this.selectedCarrierToAdd) {
      return;
    }

    const carrierId = typeof this.selectedCarrierToAdd === 'string' ?
      parseInt(this.selectedCarrierToAdd) : this.selectedCarrierToAdd;

    // Check if carrier is already selected
    if (this.selectedCarriersForCharges.some(c => c.id === carrierId)) {
      console.log('[PortChargesTab] Carrier already selected');
      this.selectedCarrierToAdd = '';
      return;
    }

    // Find the carrier and add to selection
    const carrier = this.carriers.find(c => c.id === carrierId);
    if (carrier) {
      this.selectedCarriersForCharges.push(carrier);
      console.log('[PortChargesTab] Carrier added to selection:', carrier.name);
    }

    // Reset dropdown
    this.selectedCarrierToAdd = '';
  }

  removeCarrierFromSelection(carrierId: number): void {
    this.selectedCarriersForCharges = this.selectedCarriersForCharges.filter(c => c.id !== carrierId);
    console.log('[PortChargesTab] Carrier removed from selection');
  }

  saveCarrierCharges(): void {
    if (this.selectedCarriersForCharges.length === 0) {
      alert('Please select at least one carrier');
      return;
    }

    console.log('[PortChargesTab] Saving carrier charges for:', this.selectedCarriersForCharges);
    // TODO: Implement actual save logic here

    this.closeCarrierChargesModal();
  }

  loadCarriers(): void {
    this.carrierService.getCarriers().subscribe({
      next: (carriers) => {
        this.carriers = carriers;
        console.log('[PortChargesTab] Loaded carriers:', carriers.length);
      },
      error: (error) => {
        console.error('[PortChargesTab] Error loading carriers:', error);
      }
    });
  }
}
